import { Canvas, useThree } from '@react-three/fiber'
import { Suspense, useEffect } from 'react';
import { BaseFigure as Model, FigureOverlay, ModelLoading, MouseHint } from '../../components/';
import { Center, CameraControls, Html } from '@react-three/drei';
import { theme } from '../../theme';
import { useState, useRef } from 'react';
import * as THREE from 'three'
import useAppEvent from '../../hooks/useAppEvent';

const CURVE_POINTS = 200;
const Sphere = ({ radius = 1, segments = 20, cylinderRadius = 0.75, cylinderHeight = 1.25 }) => {
  const cylinderRef = useRef(null);
  const bottomRef = useRef(null);
  const meridians = [];
  // Meridians
  for (let i = 0; i < segments; i++) {
    const rotation = Math.PI * (i / segments);
    const curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      radius, radius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    meridians.push({ g: new THREE.BufferGeometry().setFromPoints(points), r: rotation });
  }
  // Parallels
  let parallels = [];
  for (let i = 0; i < segments; i++) {
    let pOffset = radius - radius / Math.pow(2, i);
    let pRadius = Math.sqrt(radius * radius - pOffset * pOffset);
    let curve = new THREE.EllipseCurve(
      0,  0,            // ax, aY
      pRadius, pRadius,           // xRadius, yRadius
      0,  2 * Math.PI,  // aStartAngle, aEndAngle
      false,            // aClockwise
      0                 // aRotation
    );
    const points = curve.getPoints(CURVE_POINTS);
    parallels.push({ g: new THREE.BufferGeometry().setFromPoints(points), p: -pOffset });
  }
  const material = new THREE.LineBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.4});

  // Cylinder
  const cylinderMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    uniforms: {
      vlak3color1: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: `
      varying vec3 vUv; 

      void main() {
        vUv = position; 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 vlak3color1;
      varying vec3 vUv;
     
      void main() {
        // y < 0 = transparent, > 1 = opaque
        float alpha = smoothstep(-0.5, 1.0, -vUv.y);

        gl_FragColor = vec4(vlak3color1, alpha);
      }
    `, 
  });
  // Cylinder Bottom
  const cylinderBottomMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    uniforms: {
      vlak3color1: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: `
      varying vec3 vUv; 

      void main() {
        vUv = position; 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 vlak3color1;
      varying vec3 vUv;
     
      void main() {
        // y < 0 = transparent, > 1 = opaque
        float alpha = smoothstep(1.0, -1.0, -vUv.y);

        //gl_FragColor = vec4(vlak3color1, alpha);
        gl_FragColor = vec4(vlak3color1, 0.74);
      }
    `, 
  });

  const cylinderGeometry = new THREE.CylinderGeometry(cylinderRadius, cylinderRadius, cylinderHeight, 64, 1, true);
  const bottomGeometry = new THREE.CircleGeometry(cylinderRadius, 64);
  return (
    <mesh position={[0, radius - 0.01, 0]} castShadow>
      {meridians.map(({ g, r }, i) => (<line key={`mer-${i}`} rotation={[0, r, 0]} args={[g, material]} />))}
      {parallels.map(({ g, p }, i) => (<line key={`par-${i}`} position={[0, p, 0]} rotation={[Math.PI / 2, 0, 0]} args={[g, material]} />))}
      <mesh ref={cylinderRef} position={[0, -radius + cylinderHeight/2 + 0.02, 0]} args={[cylinderGeometry, cylinderMaterial]} />
      <mesh ref={bottomRef} position={[0, -radius + 0.02, 0]} rotation={[Math.PI / 2, 0, 0]} args={[bottomGeometry, cylinderBottomMaterial]} />
    </mesh>
  );
};

const Scene = ({ hasOpponent = false, cylinderRadius = 0.75, cylinderHeight = 1.25 }) => {
  const sphereRadius = 24;
  const position = hasOpponent ? [-2, sphereRadius * 2 - 1, -1.5] : [0, sphereRadius * 2 - 1, 0];
  return (
    <>
      <ambientLight intensity={1} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} shadow-mapSize={2048} castShadow />
      <Suspense fallback={<FigureOverlay customStyle={{top: '-100px', left: '-120px'}}><ModelLoading /></FigureOverlay>}>
        <Center bottom position={position}>
          <Sphere radius={sphereRadius} cylinderRadius={cylinderRadius} cylinderHeight={cylinderHeight}/>
        </Center>
      </Suspense>
    </>
  );
}

const Scene2 = ({ opponentEquipment = {}, controls = null, idleAnimationClipName = 'none', droneType, objectType, weapons, figure, objectPath, armorPath, dronePath, companionPath, consumeObjectPath, onLipsyncCreated, onPlaybackStart, onPlaybackStop, onFinished, cylinderRadius = 0.75, cylinderHeight = 1.25, showDecorators = true, decoratorsHidden = false, opponent = null, isDrunk = false  }) => {
  // This trick makes occlude work with lines
  const { raycaster } = useThree();
  raycaster.params.Line.threshold = 0.001;

  let dashedCurve = new THREE.EllipseCurve(
    0,  0,            // ax, aY
    cylinderRadius, cylinderRadius,           // xRadius, yRadius
    0,  2 * Math.PI,  // aStartAngle, aEndAngle
    false,            // aClockwise
    0                 // aRotation
  );
  const dashedMaterial = new THREE.LineDashedMaterial({color: 0xffffff, dashSize: 0.02, gapSize: 0.01});
  const dashedCircle = new THREE.BufferGeometry().setFromPoints(dashedCurve.getPoints(CURVE_POINTS));

  const hidden = !showDecorators ? true : decoratorsHidden ? true : false;
  const opponentEquipmentData = opponentEquipment;
  return (
    <>
      <ambientLight intensity={1} />
	  {/*<spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} shadow-mapSize={2048} castShadow />*/}
      <Suspense fallback={<FigureOverlay customStyle={{top: '-100px', left: '-120px'}}><ModelLoading /></FigureOverlay>}>
        <Model
          idleAnimationClipName={idleAnimationClipName}
          controls={controls}
          droneType={droneType}
          objectType={objectType}
          weapons={weapons}
          key={`figure-${figure.getId()}`}
          scale={figure.getScaleFactor()}
          glbPath={figure.getVrmSource()}
          position={[0, -1, 0]}
          armorGlbPath={armorPath}
          objectGlbPath={objectPath}
          droneGlbPath={dronePath}
          companionGlbPath={companionPath}
          consumeObjectPath={consumeObjectPath}
          isDrunk={isDrunk}
          onPlaybackStart={onPlaybackStart}
          onPlaybackStop={onPlaybackStop}
          onLipsyncCreated={onLipsyncCreated}
          onFinished={onFinished}
        />
      </Suspense>

      {opponent && <Suspense fallback={<FigureOverlay customStyle={{top: '-100px', left: '-120px'}}><ModelLoading /></FigureOverlay>}>
        <Model idleAnimationClipName={idleAnimationClipName} key={`opponent-${opponent.getId()}`} scale={opponent.getScaleFactor()} glbPath={opponent.getVrmSource() + '?opponent=true'} position={[-2, -1, -1.5]} rotation={[0, -Math.PI / 2, 0]} droneType={opponentEquipmentData.droneType} objectType={opponentEquipmentData.objectType} weapons={opponentEquipmentData.weapons} armorGlbPath={opponentEquipmentData.armorPath} objectGlbPath={opponentEquipmentData.objectPath} droneGlbPath={opponentEquipmentData.dronePath} companionGlbPath={opponentEquipmentData.companionPath}/>
      </Suspense>}

      {!hidden && <line position={[0, cylinderHeight / 8, 0]} rotation={[Math.PI / 2, 0, 0]} args={[dashedCircle, dashedMaterial]} onUpdate={(line) => line.computeLineDistances()}/>}
      <Html position={[0, cylinderHeight/8, 0]} style={{pointerEvents: 'none', transition: 'opacity linear 0.3s', opacity: hidden ?  '0.0' : '1.0'}}>
        <div style={{ width: '45vh', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginLeft: '-50%'}}>
          <img src='/assets/overlay_icon_inventory.svg' style={{width: '5vh', height: '5vh'}}/>
          <div style={{color: theme.selection, marginLeft: '-4.5vh', display: 'flex', flexDirection: 'row', fontFamily: 'BebasNeue', fontSize: '3vh', weight: '700', textShadow: `-1px -1px 0 ${theme.borderDark}, 1px -1px 0 ${theme.borderDark}, -1px 1px 0 ${theme.borderDark}, 1px 1px 0 ${theme.borderDark}` }}>
            <span>骨</span><span>头</span><span>起</span><span>来</span>
          </div>
        </div>
      </Html>
    </>
  );
}

// This is physically just a hooder for static var
class TMHolder {}

const DECORATORS_THRESHOLD_MIN = 8;
const DECORATORS_THRESHOLD_MAX = 11;
const MOVEMENT_POSITIONS = {
  'center': {
    target: { x: 0, y: -0.15, z: 0 },
    position: { x: -2.75, y: 1.25, z: -7.25 }
  },
  'home': {
    target: { x: -0.34, y: -0.10, z: 0.14 },
    position: { x: -3.37, y: 0.83, z: -7.08 }
  },
  'home_collapsed': {
    target: { x: 0.27, y: -0.16, z: -0.1 },
    position: { x: -2.48, y: 1.24, z: -7.35 }
  },
  'right': {
    target: { x: 1.92, y: -0.19, z: -0.16 },
    position: { x: -4.53, y: 0.84, z: -7.16 }
  },
  'left': {
    target: { x: -0.47, y: -0.08, z: -0.27 },
    position: { x: 3.92, y: 1.91, z: -7.89 }
  },
  'challenge': {
    target: { x: -0.98, y: -0.06, z: -0.56 },
    position: { x: 4.33, y: 1.85, z: -7.58 }
  },
  'zoom_old': {
    target: { x: 0, y: 0.50, z: 0 },
    position: { x: -0.75, y: 0.60, z: -2.25 }
  },
  'zoom': {
    target: { x: 0, y: 0.50, z: 0 },
    position: { x: 0.14, y: 0.60, z: -2.37 }
  }
};

export default function FigureView({ figure, opponent, opponentEquipment, idleAnimationClipName = 'none', objectType, droneType, weapons, objectPath, armorPath, dronePath, companionPath, width, height, noDecorators = false, controls = null, onPlaybackStart = () => {}, onPlaybackStop = () => {}, onLipsyncCreated = () => {}, isDrunk = false }) {
  const [dragging, setDragging] = useState(false);

  const [camera, setCamera] = useState(null);
  const [showDecorators, setShowDecorators] = useState(true);
  const [decoratorsHidden, setDecoratorsHidden] = useState(false);
  const sceneControlsRef = useRef(null);
  const figureControlsRef = useRef(null);
  const lightHolderRef = useRef(null);
  const cylinderRadius = 0.65;
  const cylinderHeight = 1;

  const [enabledControls, setEnableControls] = useState(true);
  const [consumeObjectPath, setConsumeObjectPath] = useState(null);
  const { subscribeToAppEvent, unsubscribeFromAppEvent, dispatchAppEvent } = useAppEvent('avatarModel');

  const moveSmoothly = (newPosition, notSmooth = false) => {
    if (!figureControlsRef.current) return;
    const {target, position} = newPosition;
    figureControlsRef.current.setTarget(target.x, target.y, target.z, !notSmooth);
    figureControlsRef.current.setPosition(position.x, position.y, position.z, !notSmooth);
  }

  const moveAvatarLeft = () => {
    setShowDecorators(true);
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['left']);
    }
  };

  const moveAvatarRight = () => {
    setShowDecorators(false);
    if (figureControlsRef.current) {
      setEnableControls(false);
      moveSmoothly(MOVEMENT_POSITIONS['right']);
    }
  };

  const homeAvatar = (collapsed = false) => {
    setShowDecorators(true);
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS[ collapsed ? 'home_collapsed' : 'home']);
    }
  }

  const centerAvatar = (forHome = false) => {
    setShowDecorators(true);
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS[forHome ? 'home' : 'center'], forHome);
    }
  };

  const zoomToFace = () => {
    setShowDecorators(false);
    if (figureControlsRef.current) {
      setEnableControls(true);
      moveSmoothly(MOVEMENT_POSITIONS['zoom']);
    }
  }

  const challengePosition = () => {
    setShowDecorators(false);
    if (figureControlsRef.current) {
      setEnableControls(false);
      moveSmoothly(MOVEMENT_POSITIONS['challenge']);
    }
  };

  if (controls) {
    controls.moveAvatarLeft = moveAvatarLeft;
    controls.moveAvatarRight = moveAvatarRight;
    controls.centerAvatar = centerAvatar;
    controls.homeAvatar = homeAvatar;
    controls.zoomToFace = zoomToFace;
    controls.moveToChallenge = challengePosition;
  }

  useEffect(() => {
    if (figureControlsRef.current) {
      figureControlsRef.current.addEventListener('update', (e) => {
        const target = new THREE.Vector3(0, 0, 0);
        const position = new THREE.Vector3(0, 0, 0);
        figureControlsRef.current.getTarget(target, false);
        figureControlsRef.current.getPosition(position, false);

        if (sceneControlsRef.current) {
          sceneControlsRef.current.setTarget(target.x, target.y, target.z);
          sceneControlsRef.current.setPosition(position.x, position.y, position.z);
        }

        if (lightHolderRef.current && camera) {
          lightHolderRef.current.quaternion.copy(camera.quaternion);
        }
        const cameraDistance = position.distanceTo(new THREE.Vector3(0, -1, 0));
        if (cameraDistance < DECORATORS_THRESHOLD_MIN || cameraDistance > DECORATORS_THRESHOLD_MAX) {
          setDecoratorsHidden(true);
        } else if (cameraDistance >= DECORATORS_THRESHOLD_MIN && cameraDistance <= DECORATORS_THRESHOLD_MAX) {
          setDecoratorsHidden(false);
        }
      });
      centerAvatar(noDecorators);
    }
  }, [figureControlsRef.current]);


  

  /**
   * Listen for Avatar Model events
   */
  useEffect(() => {
    const callback = ({ type, data }) => {
      if (type === 'consume_object') {
        setConsumeObjectPath(data);
      }
    }
    subscribeToAppEvent(callback);

    return () => unsubscribeFromAppEvent(callback);
  }, []);

  const onAvatarAnimationFinished = (animationName) => {
    if (animationName !== 'consume') {
      return;
    }
    // Clear consumable
    setConsumeObjectPath(null);
    dispatchAppEvent({
      type: 'consume_object_finished'
    });
  }

  return (
    <div style={{cursor: dragging ? 'grabbing' : 'grab', position: 'relative', width, height }} onMouseDown={() => setDragging(true)} onMouseUp={() => setDragging(false)}>
      <Canvas gl={{antialias: true, logarithmicDepthBuffer: true}} shadows={true} style={{ zIndex: 0, position: 'absolute', top: 0, left: 0, backgroundColor: theme.lightBlue, width: width,  height }} camera={{position:[-2.75, 1.25, -7.25], fov: 15 }}>
        <Scene hasOpponent={!!opponent} cylinderRadius={cylinderRadius} cylinderHeight={cylinderHeight}/>
	<CameraControls ref={sceneControlsRef} dollyToCursor={true}/>
      </Canvas>
      <Canvas gl={{antialias: true, logarithmicDepthBuffer: true}} shadows={true} onCreated={(state) => setCamera(state.camera)} style={{ zIndex: 99, position: 'absolute', top: 0, left: 0, backgroundColor: 'transparent', width: width, height }} camera={{position:[-2.75, 1.25, -7.25], fov: 15 }}>
        <group ref={lightHolderRef}>
	  {<spotLight position={[10, 0, 20]} angle={0.15} intensity={0.3} penumbra={1}/>}
        </group>
        <Scene2
          opponent={opponent}
          opponentEquipment={opponentEquipment}
          idleAnimationClipName={idleAnimationClipName}
          objectType={objectType}
          droneType={droneType}
          weapons={weapons}
          controls={controls}
          onLipsyncCreated={onLipsyncCreated}
          onPlaybackStart={onPlaybackStart}
          onPlaybackStop={onPlaybackStop}
          decoratorsHidden={decoratorsHidden || noDecorators}
          showDecorators={showDecorators}
          figure={figure}
          cylinderRadius={cylinderRadius}
          cylinderHeight={cylinderHeight}
          objectPath={objectPath}
          consumeObjectPath={consumeObjectPath}
          armorPath={armorPath}
          dronePath={dronePath}
          companionPath={companionPath}
          isDrunk={isDrunk}
          onFinished={onAvatarAnimationFinished}
        />
	<CameraControls ref={figureControlsRef} dollyToCursor={true} minDistance={1} maxDistance={100}/>
      </Canvas>
      <div style={{ position: 'absolute', bottom: 0, zIndex: 101, width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
        <MouseHint visible={showDecorators && dragging && !decoratorsHidden && !noDecorators} />
      </div>
      {noDecorators && <div className='hud-metaverse-logo-border' style={{ zIndex: 999, position: 'absolute', bottom: 0, left: 0}}/>}
    </div>
  );
}
